'use client';

import * as Sentry from '@sentry/nextjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, {
    createContext,
    Dispatch,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import { CartDto } from '@cineamo/api/models/cart/CartDto.types';
import { CartConcessionDto } from '@cineamo/api/models/cart-concession/CartConcessionDto.types';
import { CartItemDto } from '@cineamo/api/models/cart-item/CartItemDto.types';
import { ConcessionPickupLocation } from '@cineamo/api/models/cinema-configuration/CinemaConfigurationDto.types';
import { PriceOptionDto } from '@cineamo/api/models/price-option/PriceOptionDto.types';
import { ProductDto } from '@cineamo/api/models/product/ProductDto.types';
import { SeatDto } from '@cineamo/api/models/seat/SeatDto.types';
import { SeatCategoryDto } from '@cineamo/api/models/seat-category/SeatCategoryDto.types';
import { SeatLockDto } from '@cineamo/api/models/seat-lock/SeatLockDto.types';
import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { ApiResponse } from '@cineamo/api/types/ApiResponse.types';

import { Client } from '@cineamo/api/constants/Client.enum';
import { ReasonCode } from '@cineamo/api/constants/error-code-constants';

import { apiResponseHandler } from '@cineamo/api/helpers/api-response-helper/api-response-helper';
import { createCart, getCartByUuid } from '@cineamo/api/resources/cart/cart-api';
import { getCartConcessions } from '@cineamo/api/resources/cart-concession/cart-concession-api';
import { getCartItemById, getCartItems } from '@cineamo/api/resources/cart-item/cart-item-api';
import { getPriceOptionsForCart } from '@cineamo/api/resources/price-option/price-option-api';
import { getShowingSeatLocks } from '@cineamo/api/resources/seat-lock/seat-lock-api';

import { getAllListItems } from '@cineamo/ui/src/helpers/get-all-list-items';

import useLocalStorage from '@cineamo/composables/src/hooks/useLocalStorage';
import useSessionStorage from '@cineamo/composables/src/hooks/useSessionStorage';

import { Seat } from '@cineamo/ui/src/seat-map/typedefs';

import useUserStore from '@cineamo/composables/src/stores/useUserStore';
import {
    deleteCartItem,
    initAnonymousCart,
    syncSeatLocks,
    updateCartItem
} from '@cineamo/composables/src/ticket-booking/actions';

import { getCartAttribution } from '../ticket-booking/attribution';
import { concessionLineTotal } from '../ticket-booking/concession-helper';
import { SeatMapError } from '../ticket-booking/useErrorHandler';

import { useAnalytics } from './analyticsProvider';

export type SelectedSeat = {
    id: SeatDto['id'];
    priceOptionId?: PriceOptionDto['id'];
    categoryId?: SeatCategoryDto['id'];
};

interface CartProviderState {
    dto: CartDto | undefined;
    cartUuid: CartDto['uuid'];
    paidCartUuid: CartDto['uuid'];
    items: CartItemDto[];
    /** Concession lines of the cart; empty unless the cinema sells concessions. */
    concessions: CartConcessionDto[];
    setConcessions(concessions: CartConcessionDto[]): void;
    /** Where the customer collects concessions; only shown when the cinema sells them. */
    concessionPickupLocation: ConcessionPickupLocation;
    setItems: Dispatch<SetStateAction<CartItemDto[]>>;
    errors: SeatMapError[];
    setErrors: Dispatch<SetStateAction<SeatMapError[]>>;
    setPaidCartUuid: (cartUuid: CartDto['uuid']) => void;
    selectedSeats: SelectedSeat[];
    reloadCart: () => Promise<void>;
    /** Re-reads only the cart's price fields (`dto`); call after anything that changes the cart's content. */
    refreshCartDto: () => Promise<void>;
    /** Online ticketing fee in percent, which the cart applies to tickets and concessions alike; 0 while unknown. */
    onlineTicketingFee: number;
    isLoaded: boolean;
    /**
     * Cart total before gift cards. From step 01.5 on it is the API cart's `priceTotal`, while picking
     * seats, before the cart is synced, it is computed from the selection. What Stripe charges is `amountDue`.
     */
    total: number;
    /** Cents a gift card takes off the cart, as the API last computed it; 0 without one. */
    giftCardDeduction: number;
    /** What is left to pay after the gift card; the same as `total` without one. */
    amountDue: number;
    currency: string;
    seatLocks: SeatLockDto[];
    priceOptions: PriceOptionDto[];
    /** The showing's price list was fetched and is empty: nothing can be priced, so booking is not possible. */
    hasNoPriceOptions: boolean;
    showing: ShowingDto;
    isProcessing: boolean;
    setIsProcessing: Dispatch<SetStateAction<boolean>>;
    applyPriceOption(priceOptionId: PriceOptionDto['id'], cartItemId: CartItem['id']): Promise<void>;
    refetchSeatLocks(): void;
    /** `silent` skips the 'Seat Selected' event, for moves the guest did not pick seat by seat (the upsell swap). */
    selectSeat(seat: Seat, options?: { silent?: boolean }): void;
    unselectSeat(seatId: SeatDto['id']): void;
    initEmptyCart(): Promise<void>;
    currentStep: BookingStep;
    setCurrentStep: Dispatch<SetStateAction<BookingStep>>;
}

export type CartItem = CartItemDto & {
    priceOptionId?: PriceOptionDto['id'];
};

const CartContext = createContext<CartProviderState | undefined>(undefined);

export type BookingStep = '01_seat_picking' | '01.5_cart' | '02_payment' | '03_payment_complete' | '03_payment_error';

function useCartProvider() {
    const context = useContext(CartContext);
    if (!context) throw new Error('Expected to be wrapped in a CartProvider');

    return context;
}

const NO_CONCESSIONS: CartConcessionDto[] = [];

export const CartUuidStorageKey = 'cineamo-cart-uuid';

const CartProvider: React.FC<
    React.PropsWithChildren & {
        showing: ShowingDto;
        client: Client;
        isConcessionSaleEnabled?: boolean;
        concessionPickupLocation?: ConcessionPickupLocation;
    }
> = ({ children, showing, client, isConcessionSaleEnabled = false, concessionPickupLocation = 'concession_stand' }) => {
    const [cartUuid, setCartUuid] = useSessionStorage(`${CartUuidStorageKey}-${showing.id}`, '');

    // TODO: is this needed to download invoice and tickets?
    const [paidCartUuid, setPaidCartUuid] = useLocalStorage(`${CartUuidStorageKey}-paid`, '');

    const { user, isLoading: userStoreIsLoading } = useUserStore();
    const [currentStep, setCurrentStep] = useState<BookingStep>('01_seat_picking');
    const [cart, setCart] = useState<CartDto>();
    const [cartItems, setCartItems] = useState<CartItem[]>([]);
    const [errors, setErrors] = useState<SeatMapError[]>([]);
    const [isCartLoaded, setIsCartLoaded] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isSeatLocksLoaded, setIsSeatLocksLoaded] = useState(false);
    const [selectedSeats, setSelectedSeats] = useState<SelectedSeat[]>([]);
    const { track } = useAnalytics();
    const queryClient = useQueryClient();

    const concessionsQueryKey = useMemo(() => ['cart-concessions', `cartUuid-${cartUuid}`], [cartUuid]);
    const { data: concessions = NO_CONCESSIONS } = useQuery({
        queryKey: concessionsQueryKey,
        queryFn: async () => (await getCartConcessions(cartUuid))?._embedded?.concessions ?? [],
        enabled: isConcessionSaleEnabled && !!cartUuid
    });
    const setConcessions = useCallback(
        (updated: CartConcessionDto[]) => queryClient.setQueryData(concessionsQueryKey, updated),
        [queryClient, concessionsQueryKey]
    );

    const {
        data: seatLocks,
        isLoading: isSeatLocksLoading,
        refetch: refetchSeatLocks
    } = useQuery({
        queryKey: ['seat-locks', 'all', `showingId-${showing.id}`, 'isActive-true'],
        queryFn: () =>
            getAllListItems({
                queryFn: async (page: number) =>
                    await getShowingSeatLocks(showing.id, {
                        isActive: true,
                        page
                    }),
                listAccessor: 'seat-locks'
            }),
        refetchInterval: 5_000,
        enabled: !!showing.id && ['01_seat_picking', '01.5_cart'].includes(currentStep)
    });

    const {
        data: priceOptions = [],
        isFetched: isPriceOptionsFetched,
        refetch: refetchPriceOptions
    } = useQuery({
        queryKey: ['price-options', `cartUuid-${cartUuid}`, `showingId-${showing.id}`],
        queryFn: async () => {
            const result = await getAllListItems({
                queryFn: (page: number) => getPriceOptionsForCart(cartUuid, { showingId: showing.id, page }),
                listAccessor: 'price-options'
            });
            return result;
        },
        staleTime: 5 * 60 * 1000,
        enabled: !!cartUuid && !!showing.id
    });

    const hasNoPriceOptions = isPriceOptionsFetched && priceOptions.length === 0;
    const reportedShowingId = useRef<ShowingDto['id'] | undefined>(undefined);
    useEffect(() => {
        if (!hasNoPriceOptions || reportedShowingId.current === showing.id) return;
        reportedShowingId.current = showing.id;
        Sentry.captureMessage('Booking blocked: showing has no price options', {
            level: 'warning',
            tags: { showingId: showing.id, client, cinemaId: showing.cinemaId }
        });
    }, [hasNoPriceOptions, showing.id, showing.cinemaId, client]);

    const getDefaultPriceOptionForSeat = useCallback(
        (seatCategoryId?: SeatCategoryDto['id']): PriceOptionDto | undefined => {
            return priceOptions.find(
                (priceOption) =>
                    priceOption._embedded?.seatCategory?.id === seatCategoryId && priceOption.isShowingDefault
            );
        },
        [priceOptions]
    );

    const onlineTicketingFee = cart?.onlineTicketingFee ?? 0;

    const total = useMemo(() => {
        if (currentStep !== '01_seat_picking' && cart) {
            if (cart.priceTotal !== undefined) return cart.priceTotal;
        }
        const cartItemsTotal = cartItems.reduce((total, item) => {
            const priceOption = item._embedded?.product?._embedded?.priceOption;
            return total + (priceOption?.priceTotal ?? 0) * item.quantity;
        }, 0);
        const selectedSeatsTotal = selectedSeats.reduce((total, seat) => {
            const priceOption = seat.priceOptionId
                ? priceOptions.find((option) => option.id === seat.priceOptionId)
                : getDefaultPriceOptionForSeat(seat.categoryId);
            return total + (priceOption?.priceTotal ?? 0);
        }, 0);
        const concessionsTotal = concessions.reduce(
            (sum, concession) => sum + concessionLineTotal(concession, onlineTicketingFee),
            0
        );
        return (selectedSeatsTotal || cartItemsTotal) + concessionsTotal;
    }, [
        cart,
        currentStep,
        cartItems,
        selectedSeats,
        priceOptions,
        getDefaultPriceOptionForSeat,
        concessions,
        onlineTicketingFee
    ]);

    const giftCardDeduction = useMemo(
        () =>
            -(cart?.priceBreakdown ?? [])
                .filter((adjustment) => adjustment.type === 'gift_card')
                .reduce((sum, adjustment) => sum + adjustment.amount, 0),
        [cart?.priceBreakdown]
    );
    const amountDue = giftCardDeduction > 0 ? Math.max(cart?.amountDue ?? total, 0) : total;

    useEffect(() => {
        if ((seatLocks?.length ?? -1) >= 0) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: flip loaded flag once seat locks arrive
            setIsSeatLocksLoaded(true);
        }
    }, [seatLocks?.length]);
    useEffect(() => {
        // `refetch()` bypasses `enabled`, so repeat its condition here: without a cart uuid this
        // requests `/carts//price-options`, which the gateway rejects with no CORS headers.
        if (isSeatLocksLoading || !cartUuid || !showing.id) {
            return;
        }
        if (priceOptions.length === 0) {
            refetchPriceOptions();
        }
    }, [isSeatLocksLoading, cartUuid, showing.id, priceOptions.length, refetchPriceOptions]);
    useEffect(() => {
        const seatsInCart = cartItems.filter((item) => item._embedded?.product?.type === 'SeatLock');
        if (seatsInCart.length > selectedSeats.length) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: sync selection from cart items when cart grows beyond current selection
            setSelectedSeats(
                seatsInCart
                    .filter((item) => item._embedded?.product?.type === 'SeatLock')
                    .flatMap((item) => {
                        const seatId = item._embedded?.product?._embedded?.seat?.id;
                        if (seatId === undefined) {
                            return [];
                        }
                        return [
                            {
                                id: seatId,
                                priceOptionId: item._embedded?.product?._embedded?.priceOption?.id,
                                categoryId: item._embedded?.product?._embedded?.priceOption?._embedded?.seatCategory?.id
                            }
                        ];
                    })
            );
        }
        // this array intentionally does not depend on selectedSeats
        // because we want to have possibility to update selection even after seat was added to cart
    }, [cartItems, cartItems.length]);

    useEffect(() => {
        if (cart?.uuid) {
            if (cart.uuid !== cartUuid) {
                setCartUuid(cart.uuid);
            }
        }
    }, [cart?.uuid, cartUuid, setCartUuid]);

    const deleteCartItems = useCallback(async (cartUuid: string, itemsToDelete: CartItemDto[]) => {
        if (!itemsToDelete.length) {
            return;
        }

        setIsProcessing(true);

        apiResponseHandler(
            await Promise.all(itemsToDelete.map((item) => deleteCartItem(cartUuid, item.id))),
            () => {
                setCartItems((items) => items.filter((item) => !itemsToDelete.includes(item)));
            },
            (errorResponse) => {
                console.error(errorResponse);
            }
        );

        setIsProcessing(false);
    }, []);

    const initEmptyCart = useCallback(async () => {
        const attribution = getCartAttribution(showing.contentId);
        const newCart = user
            ? await createCart({ items: [], client, ...attribution })
            : await initAnonymousCart(client, attribution);
        if (!newCart) {
            console.error('Failed to create cart', newCart);
            return;
        }
        setCart(newCart);
        setCartItems([]);
        setSelectedSeats([]);
    }, [user, client, showing.contentId]);

    const onCartLoadSuccess = useCallback(
        async (cart: ApiResponse<CartDto>) => {
            const cartItems = await getAllListItems({
                queryFn: (page: number) => getCartItems(cart.uuid, { page }),
                listAccessor: 'cart-items'
            });
            setCart(cart);
            setCartItems(cartItems.filter((item) => !!item.productId));
            await deleteCartItems(
                cart.uuid,
                cartItems.filter((item) => !item.productId)
            );
        },
        [deleteCartItems]
    );

    const loadCart = useCallback(
        async (uuid: string | null) => {
            if (isCartLoaded) {
                return;
            }

            if (!uuid) {
                await initEmptyCart();
                return;
            }

            const cartResponse = await getCartByUuid(uuid);

            if (!cartResponse || cartResponse?.isPaid === true) {
                if (cartResponse?.isPaid) {
                    setPaidCartUuid(cartResponse.uuid);
                }
                await initEmptyCart();
                return;
            }

            apiResponseHandler(cartResponse, onCartLoadSuccess, async (error) => {
                if (error.data?.reason_code === ReasonCode.CART_IS_PAID) {
                    setPaidCartUuid(uuid);
                }
                await initEmptyCart();
            });
        },
        [initEmptyCart, isCartLoaded, setPaidCartUuid, onCartLoadSuccess]
    );

    // The total the cart was last reloaded for, so a gift card is only re-priced when the total moved.
    const reloadedForTotal = useRef<number | undefined>(undefined);
    const reloadCart = useCallback(async () => {
        reloadedForTotal.current = total;
        const cartResponse = await getCartByUuid(cartUuid);

        apiResponseHandler(cartResponse, onCartLoadSuccess);
    }, [cartUuid, onCartLoadSuccess, total]);

    // The API prices the gift card against the cart as it was last synced, so what is left to pay has to be
    // re-read once the seats or a price category changed (and the sync behind that settled).
    const hasGiftCard = giftCardDeduction > 0;
    useEffect(() => {
        if (currentStep !== '01.5_cart' || !hasGiftCard || isProcessing || reloadedForTotal.current === total) {
            return;
        }
        void reloadCart();
    }, [currentStep, hasGiftCard, isProcessing, total, reloadCart]);

    useEffect(() => {
        if (!isCartLoaded && !userStoreIsLoading) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: trigger async cart loading and show spinner while it runs
            setIsProcessing(true);
            loadCart(cartUuid).finally(() => {
                setIsCartLoaded(true);
                setIsProcessing(false);
            });
        }
    }, [isCartLoaded, loadCart, userStoreIsLoading, cartUuid]);

    const refreshCartDto = useCallback(async () => {
        const cartResponse = await getCartByUuid(cartUuid);

        apiResponseHandler(cartResponse, setCart);
    }, [cartUuid]);

    const findCartItemByProductId = useCallback(
        (productId: ProductDto['id']) => cartItems.find((item) => item.productId === productId),
        [cartItems]
    );

    const updateProduct = useCallback(
        async (product: ProductDto, quantity?: number) => {
            const cartItem = findCartItemByProductId(product.id);
            if (!cartItem) return;

            let updatedCartItem: CartItemDto | undefined;

            if (quantity && cartItem.quantity !== quantity) {
                const updateCartItemResponse = await updateCartItem(cartUuid, quantity);
                apiResponseHandler(
                    updateCartItemResponse,
                    (successResponse) => {
                        updatedCartItem = {
                            ...(updatedCartItem ?? cartItem),
                            quantity: successResponse.quantity
                        };
                    },
                    (errorResponse) => {
                        console.error(errorResponse);
                    }
                );
            } else {
                const cartItemResponse = await getCartItemById(cartItem.id, cartUuid);
                apiResponseHandler(
                    cartItemResponse,
                    (cartItemRefreshed) => {
                        updatedCartItem = cartItemRefreshed;
                    },
                    (errorResponse) => {
                        console.error(errorResponse);
                    }
                );
            }

            // update state
            if (updatedCartItem) {
                setCartItems((prevCartItems) =>
                    prevCartItems.map((item) => (item.id === updatedCartItem?.id ? updatedCartItem : item))
                );
            }
        },
        [cartUuid, findCartItemByProductId]
    );

    const applyPriceOption = useCallback(
        async (priceOptionId: PriceOptionDto['id'], selectedSeatId: SelectedSeat['id']) => {
            const selectedSeat = selectedSeats.find((seat) => seat.id === selectedSeatId);
            if (!selectedSeat) return;
            setIsProcessing(true);

            const updatedSelectedSeat = selectedSeats.find((seat) => seat.id === selectedSeatId);
            if (!updatedSelectedSeat) {
                setIsProcessing(false);
                return;
            }
            const oldPriceOption = priceOptions.find((option) => option.id === selectedSeat.priceOptionId);
            const newPriceOption = priceOptions.find((option) => option.id === priceOptionId);
            track('Price Option Changed', {
                newPriceOptionId: newPriceOption?.id ?? '',
                newPrice: newPriceOption?.priceTotal ?? '',
                oldPriceOptionId: oldPriceOption?.id ?? '',
                oldPrice: oldPriceOption?.priceTotal ?? '',
                seatId: selectedSeatId
            });

            updatedSelectedSeat.priceOptionId = priceOptionId;

            const updatedSelectedSeats = selectedSeats.map((seat) =>
                seat.id === selectedSeatId ? updatedSelectedSeat : seat
            );

            // Optimistic update
            setSelectedSeats(updatedSelectedSeats);

            // While seats are picked nothing is locked yet: "Weiter" sends each seat's price option with the locks,
            // and the total here prices the selection. Syncing now would lock early and start the countdown.
            if (currentStep === '01_seat_picking') {
                setIsProcessing(false);
                return;
            }

            // Sync SeatLocks includes updating the cart items
            await syncSeatLocks(cartUuid, showing.id, updatedSelectedSeats, cartItems)
                .then(() => refetchSeatLocks())
                .then(async ({ data: seatLocks }) => {
                    const seatLock = seatLocks?.find((seatLock) => seatLock._embedded?.seat?.id === selectedSeatId);

                    if (seatLock?.id) {
                        await updateProduct(seatLock);
                    } else {
                        console.error(seatLock);
                    }

                    await refreshCartDto();
                    setIsProcessing(false);
                });
        },
        [
            selectedSeats,
            cartUuid,
            showing.id,
            cartItems,
            refetchSeatLocks,
            updateProduct,
            refreshCartDto,
            track,
            priceOptions,
            currentStep
        ]
    );

    const selectSeat = useCallback<CartProviderState['selectSeat']>(
        (seat: Seat, options?: { silent?: boolean }) => {
            if (hasNoPriceOptions) return;
            const priceOption = getDefaultPriceOptionForSeat(seat.category?.id);

            if (!options?.silent) {
                track('Seat Selected', {
                    seatId: seat.id,
                    seatName: seat.positionName,
                    seatRow: seat.rowName,
                    priceOptionId: priceOption?.id ?? '',
                    price: priceOption?.priceTotal ?? ''
                });
            }
            setSelectedSeats((prev) => [
                ...prev,
                {
                    id: seat.id,
                    priceOptionId: priceOption?.id,
                    categoryId: seat.category?.id
                }
            ]);
        },
        [getDefaultPriceOptionForSeat, hasNoPriceOptions, track]
    );

    const unselectSeat = useCallback<CartProviderState['unselectSeat']>((seatId: SeatDto['id']) => {
        setSelectedSeats((prev) => prev.filter((seat) => seat.id !== seatId));
    }, []);

    const context = useMemo(
        () => ({
            dto: cart,
            cartUuid,
            paidCartUuid,
            items: cartItems,
            concessions,
            setConcessions,
            concessionPickupLocation,
            setItems: setCartItems,
            errors: errors,
            setErrors: setErrors,
            setPaidCartUuid: setPaidCartUuid,
            reloadCart,
            refreshCartDto,
            onlineTicketingFee,
            isLoaded: isCartLoaded && isSeatLocksLoaded,
            total,
            giftCardDeduction,
            amountDue,
            currency: 'EUR', // TODO: remove hardcoded currency
            refetchSeatLocks,
            selectedSeats,
            isProcessing,
            setIsProcessing,
            applyPriceOption,
            selectSeat,
            unselectSeat,
            seatLocks: seatLocks ?? [],
            showing,
            priceOptions,
            hasNoPriceOptions,
            initEmptyCart,
            currentStep,
            setCurrentStep
        }),
        [
            cart,
            cartUuid,
            paidCartUuid,
            cartItems,
            concessions,
            setConcessions,
            concessionPickupLocation,
            setCartItems,
            errors,
            setErrors,
            setPaidCartUuid,
            reloadCart,
            refreshCartDto,
            onlineTicketingFee,
            isCartLoaded,
            isSeatLocksLoaded,
            total,
            giftCardDeduction,
            amountDue,
            refetchSeatLocks,
            selectedSeats,
            isProcessing,
            setIsProcessing,
            applyPriceOption,
            selectSeat,
            unselectSeat,
            seatLocks,
            showing,
            priceOptions,
            hasNoPriceOptions,
            initEmptyCart,
            currentStep,
            setCurrentStep
        ]
    );

    return <CartContext.Provider value={context}>{children}</CartContext.Provider>;
};

export { useCartProvider, CartProvider };
